import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';

const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition";

const invalidInputClass =
  "block w-full rounded-lg border border-red-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 focus:outline-none transition";

// Mirrors Announcement#cta_fields_are_paired. The server stays the authority —
// this only puts the message next to the field that is missing, which a
// redirect-and-flash round trip cannot do.
function ctaPairingErrors(label, url) {
  return {
    cta_label: label.length === 0 && url.length > 0
      ? "Button label is required when a call-to-action link is entered."
      : null,
    cta_url: url.length === 0 && label.length > 0
      ? "Button link is required when a call-to-action label is entered."
      : null,
  };
}

export default function AnnouncementForm({
  announcement,
  mode,
  availableLicenseTypes = [],
  availableFacilityTypes = [],
}) {
  const [title, setTitle] = useState(announcement.title || '');
  const [description, setDescription] = useState(announcement.description || '');
  const [ctaLabel, setCtaLabel] = useState(announcement.cta_label || '');
  const [ctaUrl, setCtaUrl] = useState(announcement.cta_url || '');
  const [licenseTypes, setLicenseTypes] = useState(announcement.license_types || []);
  const [facilityTypes, setFacilityTypes] = useState(announcement.facility_types || []);
  const [ctaErrors, setCtaErrors] = useState({ cta_label: null, cta_url: null });
  const [submitting, setSubmitting] = useState(false);

  const isEdit = mode === 'edit';
  const isUntargeted = licenseTypes.length === 0 && facilityTypes.length === 0;

  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const trimmedLabel = ctaLabel.trim();
    const trimmedUrl = ctaUrl.trim();
    const pairingErrors = ctaPairingErrors(trimmedLabel, trimmedUrl);
    setCtaErrors(pairingErrors);
    if (pairingErrors.cta_label || pairingErrors.cta_url) return;

    setSubmitting(true);

    const payload = {
      announcement: {
        title: title.trim(),
        description: description.trim(),
        cta_label: trimmedLabel,
        cta_url: trimmedUrl,
        // Always send at least one entry so clearing every checkbox actually
        // clears the targeting server-side.
        license_types: licenseTypes.length > 0 ? licenseTypes : [''],
        facility_types: facilityTypes.length > 0 ? facilityTypes : [''],
      },
    };

    const options = { onFinish: () => setSubmitting(false) };
    if (isEdit) {
      router.patch(`/superadmin/announcements/${announcement.id}`, payload, options);
    } else {
      router.post('/superadmin/announcements', payload, options);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
        <h2 className="text-lg font-semibold text-gray-900">Announcement</h2>

        <div>
          <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-1.5">Title</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            placeholder="e.g. New continuing education requirements for 2027"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-1.5">Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            rows={4}
            placeholder="What you want users to know."
            className={inputClass}
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Call to action (optional)</h2>
          <p className="mt-1 text-sm text-gray-500">
            Leave both fields blank for no button, or complete both fields to add one.
          </p>
        </div>

        <div>
          <label htmlFor="cta_label" className="block text-sm font-semibold text-gray-700 mb-1.5">
            Button label
          </label>
          <input
            type="text"
            id="cta_label"
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            placeholder="e.g. Read the bulletin"
            aria-invalid={ctaErrors.cta_label ? 'true' : undefined}
            aria-describedby={ctaErrors.cta_label ? 'cta_label_error' : undefined}
            className={ctaErrors.cta_label ? invalidInputClass : inputClass}
          />
          {ctaErrors.cta_label && (
            <p id="cta_label_error" className="mt-1 text-xs font-medium text-red-600">
              {ctaErrors.cta_label}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="cta_url" className="block text-sm font-semibold text-gray-700 mb-1.5">
            Button link
          </label>
          <input
            type="url"
            id="cta_url"
            value={ctaUrl}
            onChange={(e) => setCtaUrl(e.target.value)}
            placeholder="https://example.com/bulletin"
            aria-invalid={ctaErrors.cta_url ? 'true' : undefined}
            aria-describedby={ctaErrors.cta_url ? 'cta_url_error' : undefined}
            className={ctaErrors.cta_url ? invalidInputClass : inputClass}
          />
          {ctaErrors.cta_url && (
            <p id="cta_url_error" className="mt-1 text-xs font-medium text-red-600">
              {ctaErrors.cta_url}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Must be an http:// or https:// address. Opens in a new tab.
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Audience</h2>
          <p className="mt-1 text-sm text-gray-500">
            Leave everything unchecked to publish to all users. Selections are combined with OR —
            a user sees the announcement if they match any selected license type or any selected facility type.
          </p>
        </div>

        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            isUntargeted
              ? 'border-indigo-200 bg-indigo-50 text-indigo-800'
              : 'border-gray-200 bg-gray-50 text-gray-700'
          }`}
        >
          {isUntargeted
            ? 'This announcement will be shown to all users.'
            : `Targeting ${licenseTypes.length} license type(s) or ${facilityTypes.length} facility type(s).`}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">License types</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {availableLicenseTypes.map((type) => (
              <label
                key={type}
                className="flex items-start gap-2.5 rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={licenseTypes.includes(type)}
                  onChange={() => toggle(licenseTypes, setLicenseTypes, type)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <span className="text-sm text-gray-800">{type}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Facility types</h3>
          <div className="grid gap-2">
            {availableFacilityTypes.map((facilityType) => (
              <label
                key={facilityType.value}
                className="flex items-start gap-2.5 rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={facilityTypes.includes(facilityType.value)}
                  onChange={() => toggle(facilityTypes, setFacilityTypes, facilityType.value)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <span className="text-sm text-gray-800">{facilityType.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3">
        <Link
          href="/superadmin/announcements"
          className="flex-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
        >
          {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Publish announcement'}
        </button>
      </div>
    </form>
  );
}
